// ABOUTME: RMC shipping date cell with variation code visual treatments for the orders table.
// Extracts rendering logic from Order.rows.tsx to enable hook usage (useVariationCode).

import {dateFormatter, timeFormatter, Typography} from '@hconnect/uikit'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useVariationCode} from '../hooks/useVariationCode'
import type {Order} from '../Order.types'
import {getEventLocationOffset} from '../Order.utils'
import {VARIATION_CODE_DANGER_COLOR} from '../variationCode.utils'

import {TimezoneWarning} from './TimezoneWarning'

interface RmcShippingDateCellProps {
  order: Order
  userOffset: number
}

export const RmcShippingDateCell: React.FC<RmcShippingDateCellProps> = ({order, userOffset}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()

  const eventLocationOffset = getEventLocationOffset(order)

  const {
    showDateAsterisk,
    showDateDanger,
    showAdjustedTimeAsPrimary,
    adjustedVariationTime,
    showRequestedDateTimeWithStrikeThrough
  } = useVariationCode(order, eventLocationOffset)

  if (!order.shippingDate) {
    return (
      <Typography color="textPrimary" variant="subtitle1">
        {'-'}
      </Typography>
    )
  }

  const scheduleStartDate = order.schedules?.[0]?.scheduleStartDate ?? order.shippingDate

  const formattedDatePart = showAdjustedTimeAsPrimary && order.variationCodeAdjustedTime
    ? dateFormatter(order.variationCodeAdjustedTime, language, eventLocationOffset)
    : dateFormatter(order.shippingDate, language, eventLocationOffset)

  const formattedTimePart = showAdjustedTimeAsPrimary && order.variationCodeAdjustedTime
    ? timeFormatter(order.variationCodeAdjustedTime, language, eventLocationOffset)
    : timeFormatter(scheduleStartDate, language, eventLocationOffset)

  const dateAsterisk = showDateAsterisk ? ' *' : ''
  const dateSpanStyle = showDateDanger ? {color: VARIATION_CODE_DANGER_COLOR} : undefined

  return (
    <>
      <span>
        {formattedDatePart},{' '}
        {showRequestedDateTimeWithStrikeThrough ? (
          <>
            <span>
              {adjustedVariationTime}
              {dateAsterisk}
            </span>
            {' '}
            <span style={{textDecoration: 'line-through'}}>{formattedTimePart}</span>
          </>
        ) : (
          <span style={dateSpanStyle}>
            {formattedTimePart}
            {dateAsterisk}
          </span>
        )}
      </span>
      <TimezoneWarning
        t={t}
        order={order}
        userOffset={userOffset}
        eventLocationOffset={eventLocationOffset}
      />
    </>
  )
}
